import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAppProvider } from "../AppContext";
import {
  MempoolUtxoProvider,
  singleSendNft,
  toTokenAddress,
  UnisatSigner,
} from "@cat-protocol/cat-sdk";
import {
  CollectionItem,
  getExplorerServiceUrl,
  getTxStatus,
  NFTItem,
  patchGetUtxos,
  TxStatus,
} from "../api";

import { useInterval } from "usehooks-ts";
import { StorageAPI } from "../storage";
import MyChainProvider from "../MyChainProvider";

interface SwapNftDialogProps {
  nftItem: NFTItem;
  collectionItem: CollectionItem;
  handleClose: () => void;
}

enum UpgradeStatus {
  INITIAL,
  REQUEST_SIGN,
  SENT,
  SUCCESS,
  FAILED,
}

function getStatusText(status: UpgradeStatus) {
  switch (status) {
    case UpgradeStatus.REQUEST_SIGN:
      return "Requesting sign ...";
    case UpgradeStatus.SENT:
      return "Sent";
    case UpgradeStatus.SUCCESS:
      return "Upgrade successful";
    case UpgradeStatus.FAILED:
      return "Failed";
  }
}


export default function SwapNftDialog(props: SwapNftDialogProps) {
  const { connected, connectedAccount, network, feeRate } = useAppProvider();

  const [_error, setError] = useState("");

  const [_swapTxId, setSwapTxId] = useState("");

  const [_status, setStatus] = useState(UpgradeStatus.REQUEST_SIGN);

  const { nftItem, collectionItem } = props;


  useEffect(() => {
    const upgradeNft = async (upgradeAddress: string) => {
      try {
        const address = connectedAccount!.payment.address;

        const chainProvider = new MyChainProvider(address, collectionItem.collectionId, network);
        const utxoProvider = new MempoolUtxoProvider(network);
        patchGetUtxos(utxoProvider)      

        const signer = new UnisatSigner(window.unisat);

        const sendRes = await singleSendNft(
          signer,
          utxoProvider,
          chainProvider,
          collectionItem.minterAddr,
          [nftItem.utxo],
          [
            toTokenAddress(upgradeAddress)
          ],
          feeRate
        );

        const txId = sendRes.sendTx.extractTransaction().getId();
        setSwapTxId(txId);

        setStatus(UpgradeStatus.SENT);

        StorageAPI.addOrUpdateHistoryItem(network, {
          type: 'CAT721',
          amountOrLocalId: nftItem.localId.toString(),
          symbol: collectionItem.metadata.symbol,
          txId: txId,
          status: TxStatus.initial,
        });
      } catch (error) {
        console.error("swapNft error", error);
        if (error instanceof Error) {
          console.error("setError error", error);
          setError((error as Error).message);
        } else if (typeof error === "string") {
          console.error("swapNft error", error);
          setError(String(error));
        } else if (error && typeof error === "object" && "message" in error) {
          console.error("error", error);
          setError((error as any).message);
        } else {
          console.error("error", error);
          setError("unknown error");
        }
      }
    };

    if (connected()) {
      upgradeNft(collectionItem.paymentAddr);
    }
  }, [connected, network, collectionItem, feeRate, connectedAccount, nftItem]);

  useInterval(
    () => {
      // Your custom logic here

      getTxStatus(_swapTxId, network)
        .then((status) => {
          StorageAPI.updateHistoryItemTxStatus(network, _swapTxId, status);
          if (status === TxStatus.finished) {
            setStatus(UpgradeStatus.SUCCESS);
          } else if (status === TxStatus.failed) {
            setStatus(UpgradeStatus.FAILED);
          }
        })
        .catch((e) => {
          console.error("getTxStatus:", e);
        });
    },
    // Delay in milliseconds or null to stop it
    _status === UpgradeStatus.SENT && _swapTxId ? 5000 : null
  );

  return (
    <React.Fragment>
      <Dialog
        sx={{ zIndex: 1800 }}
        disableEscapeKeyDown={true}
        open={true}
        fullWidth
        //onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Upgrade NFT
        </DialogTitle>
        <DialogContent>
          <Box>
            {_error ? (
              <DialogContentText id="alert-dialog-error" color="red" mt={1}>
                {`Error: ${_error}`}
              </DialogContentText>
            ) : (
              <DialogContentText id="alert-dialog-description" mt={1}>
                {_status === UpgradeStatus.SENT && _swapTxId ? (
                  <>
                    {`Sending ${collectionItem.metadata.symbol}:${nftItem.localId} NFT to CAT Protocol Upgrade in tx:`}
                    <Link
                      href={`${getExplorerServiceUrl(network)}/tx/${_swapTxId}`}
                      rel="noopener noreferrer"
                      target="_blank"
                      ml={1}
                      variant="body2"
                    >
                      {_swapTxId?.substring(0, 6) + "..."}
                    </Link>
                  </>
                ) : (
                  getStatusText(_status)
                )}
              </DialogContentText>
            )}
          </Box>

          {_status === UpgradeStatus.SENT ?  <CircularProgress size={26} sx={{mt: 1}}></CircularProgress> : <></>}

          <DialogContentText id="alert-dialog-tips" color="grey" mt={1}>
            {`This upgrade involves NFT ${
                collectionItem.metadata.symbol
              }:${nftItem.localId}`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.handleClose} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
